import { EDITOR_CONSTANTS } from "@perceptron/constants";
import type { Editor, JSONContent } from "@tiptap/react";
import { isEqual } from "lodash-es";

import { DEFAULT_MEDIA_SET, useMediaStore } from "@demo/store/media";
import { Role } from "@demo/types/base";
import type { DocumentInputContentItem } from "@demo/types/documentContent";
import { DocumentContentProcessor } from "@demo/utils/document-content-processor";

export const BLANK: JSONContent = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

export const BLANK_WITH_MEDIA_SET_CONTAINER: JSONContent = {
  type: "doc",
  content: [
    {
      type: "mediaSetContainer",
      attrs: {
        id: DEFAULT_MEDIA_SET.id,
        mediaIds: [],
      },
    },
  ],
};

/**
 * Moves the caret into the first paragraph after the media container.
 * Does nothing when the container is gone or nothing follows it — landing the
 * caret above would be the jump this exists to avoid.
 */
const focusParagraphAfterMedia = (editor: Editor, mediaSetId: string) => {
  if (typeof editor.state?.doc?.descendants !== "function") return;

  const doc = editor.state.doc;

  let end: number | null = null;
  doc.descendants((node, nodePos) => {
    if (end !== null) return false;
    if (node.type.name === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER && node.attrs?.id === mediaSetId) {
      end = nodePos + node.nodeSize;
      return false;
    }
    return true;
  });
  if (end === null) return;
  const containerEnd: number = end;

  let target: number | null = null;
  doc.descendants((node, nodePos) => {
    if (target !== null) return false;
    if (nodePos >= containerEnd && node.type.name === EDITOR_CONSTANTS.NODE_TYPES.PARAGRAPH) {
      target = nodePos + 1;
      return false;
    }
    return true;
  });
  if (target === null) return;

  editor.chain().focus().setTextSelection(target).run();
};

/**
 * After inserting a container: the caret belongs below it.
 *
 * Inserting at a caret that already had text appends the block and leaves the
 * selection where it was, in the paragraph above, so this moves it regardless
 * of where it currently sits.
 */
export const focusParagraphAfterInsertedMedia = (editor: Editor, mediaSetId: string) => {
  focusParagraphAfterMedia(editor, mediaSetId);
};

/**
 * After an upload settles: the caret belongs below the container only if the
 * user has not put it somewhere themselves.
 *
 * This runs from a `finally` long after the files were chosen, by which time
 * they may have clicked into any paragraph and started typing. A caret in a
 * paragraph is one they placed; a caret on the container is not.
 */
export const focusNextParagraphAfterMediaIfNeeded = (editor: Editor, mediaSetId: string) => {
  if (typeof editor.state?.doc?.descendants !== "function") return;

  const { from } = editor.state.selection || { from: 0 };
  const parent = editor.state.doc.resolve(from)?.parent;
  if (parent?.type.name === EDITOR_CONSTANTS.NODE_TYPES.PARAGRAPH) return;

  focusParagraphAfterMedia(editor, mediaSetId);
};

/**
 * Checks whether the TipTap document is empty, text- and media-wise.
 *
 * Runs on every editor update (it gates the run button and the inline
 * examples), so it walks the JSON directly instead of running the full
 * DocumentContentProcessor conversion — same semantics: any non-empty text
 * node (or hard break) counts as text, and a media set container counts as
 * media when its store-backed set actually holds media.
 */
export const isTiptapContentEmpty = (
  json: JSONContent,
): {
  text: boolean;
  media: boolean;
} => {
  const { mediaSets } = useMediaStore.getState();
  let hasText = false;
  let hasMedia = false;

  const walk = (node: JSONContent) => {
    if (hasText && hasMedia) return;

    if ((node.type === "text" && node.text) || node.type === "hardBreak") {
      hasText = true;
    } else if (node.type === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER && node.attrs?.id) {
      const mediaSet = mediaSets.find((set) => set.id === node.attrs?.id);
      if (mediaSet && mediaSet.media.length > 0) {
        hasMedia = true;
      }
    }

    node.content?.forEach(walk);
  };

  walk(json);

  return { text: !hasText, media: !hasMedia };
};

/**
 * Checks if a DocumentContent array represents empty content.
 * Only text content with empty/whitespace values is considered empty;
 * images and videos always make content non-empty.
 */
const isDocumentContentEmpty = (content: DocumentInputContentItem[]): { text: boolean; media: boolean } => {
  if (!content || content.length === 0) return { text: true, media: true };

  const hasText = content.some((item) => item.type === "paragraph" && item.value.type === "text" && item.value.value);
  const hasMedia = content.some((item) => item.type === "media" && item.value.length > 0);

  return { text: !hasText, media: !hasMedia };
};

/**
 * Compares current editor content with saved DocumentContent to determine if content is dirty.
 * Comparing the processed DocumentContent (not raw TipTap JSON) is more reliable because it
 * uses stable media UUIDs instead of ephemeral mediaSetIds and mirrors the API data structure.
 *
 * @param currentJson - Current TipTap editor content
 * @param savedDocumentContent - Previously saved DocumentContent from API
 * @param role - Editor role (user or system)
 * @returns true if content has changed, false if identical
 */
export const isDocumentContentDirty = (
  currentJson: JSONContent,
  savedDocumentContent: DocumentInputContentItem[],
  role: Role,
): boolean => {
  const processor = new DocumentContentProcessor();
  const currentDocumentContent = processor.processJsonToContent(currentJson, role);

  const isCurrentEmpty = isDocumentContentEmpty(currentDocumentContent);
  const isSavedEmpty = isDocumentContentEmpty(savedDocumentContent);

  // Both completely empty (no text and no media) = not dirty
  if (isCurrentEmpty.text && isCurrentEmpty.media && isSavedEmpty.text && isSavedEmpty.media) {
    return false;
  }

  return !isEqual(currentDocumentContent, savedDocumentContent);
};
